<template>
  <div class="dashboard-panel p-financing">
    <div>
      <h2>Financing Type</h2>

      <Tooltip
        v-if="tooltipModel"
        :tooltipModel="tooltipModel"
        :customTextFormat="formatTooltipText"
        elQuery="#chart-financing"
      />
      <Graph
        v-if="matches.length"
        id="chart-financing"
        :chartData="graphData"
        :options="graphOptions"
      />

      <p v-else>
        No data found with this criteria
      </p>

      <div class="grid-wrapper">
        <div
          v-for="(type, index) in availableTypes"
          :key="index"
          class="help big"
        >
          <div class="circle" :class="[`color-${index + 1}`]" />
          <span>{{ type.text || type.key }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Graph from './Graph'
import Tooltip from './Tooltip'

export default {
  name: 'PanelFinancing',
  components: {
    Graph,
    Tooltip,
  },
  props: {
    matches: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    total: 0,
    availableTypes: [
      { key: 'External Acquisition (buy out)', text: 'External Acquisition' },
      { key: 'Refinance' },
      { key: 'Internal Acquisition (buy in)', text: 'Internal Aquisition' },
      { key: 'Commercial Building & Renovation', text: 'Commercial Building' },
      { key: 'Working Capital' },
      { key: 'Recruiting' },
    ],
    typeColors: [
      '#327996',
      '#194253',
      '#0096A6',
      '#B0DDF0',
      '#1F78B4',
      '#18C8DB',
    ],
    graphData: {},
    graphOptions: {
      cutoutPercentage: 80,
      layout: {
        padding: {
          bottom: 40,
          left: 40,
          right: 40,
        },
      },
      legend: {
        display: false,
      },
      tooltips: {
        enabled: false,
      },
    },
    tooltipModel: null,
  }),

  watch: {
    matches () {
      this.buildGraphData()
    },
  },

  mounted () {
    this.graphOptions.tooltips.custom = tooltipModel => {
      this.tooltipModel = tooltipModel
    }

    this.buildGraphData()
  },

  methods: {
    async buildGraphData () {
      if (!this.matches) return

      const { colors, counts, labels } = this.parseMatchesData(this.matches)

      this.graphData = {
        labels,
        datasets: [{
          data: counts,
          backgroundColor: colors,
        }],
      }
    },

    parseMatchesData (matches) {
      const optionIndexes = {}
      this.availableTypes.forEach(({ key }, index) => {
        optionIndexes[key] = index
      })

      this.total = 0
      const counts = [...Array(6)].map((_, i) => ({
        index: i,
        count: 0,
      }))

      matches.forEach(({ deal }) => {
        const dealType = Array.isArray(deal.dealType) ? deal.dealType : [deal.dealType]

        dealType
          .filter(type => !!type)
          .forEach(type => {
            counts[optionIndexes[type]].count += 1
            this.total++
          })
      })

      counts.sort((a, b) => {
        if (a.count < b.count) return 1
        if (a.count > b.count) return -1
        return 0
      })

      const labels = []
      const colors = []
      counts.forEach(({ index }) => {
        const type = this.availableTypes[index]
        labels.push(type.text || type.key)
        colors.push(this.typeColors[index])
      })

      return {
        counts: counts.map(({ count }) => count),
        labels,
        colors,
      }
    },

    formatTooltipText (val) {
      const label = val.slice(0, val.indexOf(':'))
      const percent = Math.floor((Number(val.match(/\d+$/)[0]) / this.total) * 100)
      return [`${percent}%`, label]
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-panel > div {
  width: 375px;
  padding: 20px 15px;
}

.grid-wrapper {
  grid-gap: 15px 20px;
  justify-content: space-around;

  & > div {
    display: flex;
    align-items: center;
  }
}

.circle {
  height: 10px;
  width: 10px;
  border-radius: 50%;

  margin-right: 5px;

  &.color-1 { background-color: $graphColor1; }
  &.color-2 { background-color: $graphColor2; }
  &.color-3 { background-color: $graphColor3; }
  &.color-4 { background-color: $graphColor4; }
  &.color-5 { background-color: $graphColor5; }
  &.color-6 { background-color: $graphColor6; }
}
</style>
